package com.arenaproplay.admin.ui.layouts.shell

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.navigation.Routes
import com.arenaproplay.admin.state.auth.LocalAuthSession
import com.arenaproplay.admin.state.shell.ShellCounts
import com.arenaproplay.admin.ui.components.icons.LucideIcon
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.utils.sBorderRadius
import org.jetbrains.compose.web.css.*

/**
 * Night Arena v2 — 72 px icon rail sidebar.
 *
 * Layout (top → bottom):
 *   • 40×40 gradient logo chip ("A", Unbounded)
 *   • icon buttons for the 9 IA surfaces (with CSS tooltip on hover)
 *   • [spacer that pushes pinned items to bottom]
 *   • Настройки gear (pinned)
 *   • Admin avatar chip (pinned)
 *
 * Active state: rgba(0,97,255,.22) background + #8FB8FF icon colour.
 * Live badge dot on Входящие if [counts.supportCount] > 0.
 * Live pulse dot on Центр to indicate online presence count.
 *
 * Mobile (<920px): rail is HIDDEN entirely via display:none.
 * A hamburger in the mobile topbar opens a slide-in drawer (MobileNavDrawer).
 *
 * NOTE: [isOpen] / [onToggle] are kept for AdminLayout compat but not used
 * — the rail is always 72px; there is no expand/collapse in the new design.
 */
@Composable
fun Sidebar(
    isOpen: Boolean,
    onToggle: () -> Unit,
    counts: ShellCounts = ShellCounts(),
    onSearchClick: (() -> Unit)? = null,
) {
    val ctx = rememberPageContext()
    val currentRoute = ctx.route.path

    // Inject mobile CSS once per page (idempotent — style tag with fixed id).
    SidebarMobileStyles()

    Column(
        Modifier
            .width(72.px)
            .height(100.percent)
            .styleModifier {
                property("background", "#070C16")
                property("border-right", "1px solid rgba(255,255,255,.06)")
                property("flex-shrink", "0")
                property("position", "relative")
                property("z-index", "100")
            }
            .classNames("na-rail"),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // ── Logo chip ──────────────────────────────────────────────────────────
        Box(Modifier.height(18.px))
        LogoChip()
        Box(Modifier.height(14.px))

        // ── Main nav items (middle section, no scroll — 9 items fit any ≥640px viewport)
        // overflow must NOT be set (or set to Visible) so CSS tooltips positioned at
        // calc(100% + 12px) can escape the rail column without being clipped.
        Column(
            Modifier
                .fillMaxWidth()
                .weight(1f),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            appNavGroups.forEach { group ->
                group.items.forEach { item ->
                    val isActive = isActiveRoute(currentRoute, item)
                    val badgeCount: Int? = when {
                        item.route == Routes.INBOX -> counts.supportCount
                        else -> null
                    }
                    val showPulse = item.route == Routes.HOME && (counts.onlineCount ?: 0) > 0

                    RailItem(
                        icon       = item.icon,
                        label      = item.label,
                        active     = isActive,
                        badgeCount = badgeCount,
                        showPulse  = showPulse,
                        onClick    = {
                            val target = item.legacySidebarTarget ?: item.route
                            ctx.router.navigateTo(target)
                        }
                    )
                    Box(Modifier.height(4.px))
                }
            }
        }

        // ── Pinned bottom items ────────────────────────────────────────────────
        Box(Modifier.height(8.px))
        RailItem(
            icon   = settingsNavItem.icon,
            label  = settingsNavItem.label,
            active = currentRoute == settingsNavItem.route,
            onClick = { ctx.router.navigateTo(settingsNavItem.route) }
        )
        Box(Modifier.height(10.px))
        AvatarChip()
        Box(Modifier.height(14.px))
    }
}

// ── Active route detection ─────────────────────────────────────────────────────

private fun isActiveRoute(currentRoute: String, item: NavItem): Boolean {
    // Exact match on the nav route
    if (currentRoute == item.route) return true
    // Also highlight if current page is the legacy target this item points to
    val legacy = item.legacySidebarTarget
    if (legacy != null && currentRoute == legacy) return true
    // Prefix match for detail sub-pages (e.g. /users/123, /clubs/456)
    if (item.route != Routes.HOME && currentRoute.startsWith(item.route + "/")) return true
    return false
}

// ── Logo chip ─────────────────────────────────────────────────────────────────

@Composable
private fun LogoChip() {
    Box(
        Modifier
            .width(40.px)
            .height(40.px)
            .sBorderRadius(12)
            .classNames("na-rail-logo")
            .styleModifier {
                property("background", "linear-gradient(135deg,#0061FF,#4D8CFF)")
                property("box-shadow", "0 4px 16px rgba(0,97,255,.4)")
                property("flex-shrink", "0")
                property("cursor", "default")
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            "A",
            Modifier.styleModifier {
                property("font-family", "'Unbounded', sans-serif")
                property("font-weight", "700")
                property("font-size", "16px")
                property("color", "#FFFFFF")
                property("line-height", "1")
            }
        )
    }
}

// ── Rail item ─────────────────────────────────────────────────────────────────

@Composable
private fun RailItem(
    icon: String,
    label: String,
    active: Boolean,
    badgeCount: Int? = null,
    showPulse: Boolean = false,
    onClick: () -> Unit,
) {
    // Wrapper for tooltip positioning
    Box(
        Modifier
            .styleModifier { property("position", "relative") }
            .classNames("na-tip-wrap"),
        contentAlignment = Alignment.Center
    ) {
        // ── Icon button ──────────────────────────────────────────────────────
        Box(
            Modifier
                .width(40.px)
                .height(40.px)
                .sBorderRadius(12)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    if (active) {
                        property("background", "rgba(0,97,255,.22)")
                        property("color", "#8FB8FF")
                    } else {
                        property("background", "transparent")
                        property("color", "rgba(240,244,252,.55)")
                    }
                    property("transition", "background 140ms ease, color 140ms ease, transform 100ms ease")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("position", "relative")
                }
                .classNames("na-rail-btn")
                .onClick { onClick() }
        ) {
            LucideIcon(
                name  = icon,
                size  = 18,
                color = if (active) "#8FB8FF" else "rgba(240,244,252,.55)"
            )

            // Live badge dot (e.g. unread support messages)
            if (badgeCount != null && badgeCount > 0) {
                Box(
                    Modifier
                        .width(8.px)
                        .height(8.px)
                        .borderRadius(50.percent)
                        .styleModifier {
                            property("background", "#FF6B5B")
                            property("position", "absolute")
                            property("top", "7px")
                            property("right", "7px")
                            property("box-shadow", "0 0 0 2px #070C16")
                        }
                )
            }

            // Online pulse dot
            if (showPulse) {
                Box(
                    Modifier
                        .width(7.px)
                        .height(7.px)
                        .borderRadius(50.percent)
                        .styleModifier {
                            property("background", "#00E5A4")
                            property("position", "absolute")
                            property("bottom", "7px")
                            property("right", "7px")
                            property("box-shadow", "0 0 0 2px #070C16, 0 0 8px #00E5A4")
                            property("animation", "na-pulse 2s ease-in-out infinite")
                        }
                )
            }
        }

        // ── CSS tooltip ──────────────────────────────────────────────────────
        Box(
            Modifier
                .styleModifier {
                    property("position", "absolute")
                    property("left", "calc(100% + 12px)")
                    property("top", "50%")
                    property("transform", "translateY(-50%)")
                    property("background", "#0B1220")
                    property("border", "1px solid rgba(255,255,255,.08)")
                    property("border-radius", "8px")
                    property("padding", "5px 10px")
                    property("white-space", "nowrap")
                    property("pointer-events", "none")
                    property("opacity", "0")
                    property("transition", "opacity 120ms ease")
                    property("z-index", "500")
                    property("box-shadow", "0 4px 16px rgba(0,0,0,.35)")
                }
                .classNames("na-tip")
        ) {
            SpanText(
                label,
                Modifier.styleModifier {
                    property("font-size", "12px")
                    property("font-weight", "500")
                    property("color", "#F0F4FC")
                    property("font-family", "'Inter', sans-serif")
                }
            )
        }
    }
}

// ── Avatar chip (bottom-pinned) ───────────────────────────────────────────────

@Composable
private fun AvatarChip() {
    val session = LocalAuthSession.current
    val user = session.user
    val name = user?.name ?: user?.email?.substringBefore("@") ?: "A"
    val initial = name.firstOrNull()?.uppercaseChar()?.toString() ?: "A"

    Box(
        Modifier
            .width(32.px)
            .height(32.px)
            .borderRadius(50.percent)
            .classNames("na-rail-avatar")
            .styleModifier {
                property("background", AppColors.AvatarGradient)
                property("flex-shrink", "0")
                property("cursor", "default")
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            initial,
            Modifier.styleModifier {
                property("font-size", "12px")
                property("font-weight", "600")
                property("color", "#FFFFFF")
                property("line-height", "1")
            }
        )
    }
}

// ── Rail tooltip + hover CSS ──────────────────────────────────────────────────

@Composable
private fun SidebarMobileStyles() {
    val id = "na-rail-styles"
    // Idempotent — only injected once per page.
    androidx.compose.runtime.LaunchedEffect(Unit) {
        val existing = kotlinx.browser.document.getElementById(id)
        if (existing != null) return@LaunchedEffect
        val style = kotlinx.browser.document.createElement("style")
        style.id = id
        style.textContent = """
            /* Rail button hover: show tooltip + scale press feedback */
            .na-tip-wrap:hover .na-tip { opacity: 1; }
            .na-rail-btn:active { transform: scale(0.96); }

            /* Pulse keyframe for online dot */
            @keyframes na-pulse {
                0%, 100% { opacity: 1; }
                50%       { opacity: 0.35; }
            }

            /* Mobile (<920px): hide rail entirely — drawer takes over */
            @media (max-width: 920px) {
                .na-rail { display: none !important; }
            }
        """.trimIndent()
        kotlinx.browser.document.head?.appendChild(style)
    }
}
